import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { CONTRACTS, ABIS } from '../utils/contracts';
import { txOverrides } from '../utils/tx';
import { useNotices } from '../hooks/useNotices';
import { formatNumber } from '../utils/formatters';

// ---------------------------------------------------------------------------
// The bell: wallet-connect notices that would otherwise be page banners.
// Every notice is a real recoverable balance with a one-click (few-signature,
// narrated) recovery. Zero page space when there's nothing to say.
// ---------------------------------------------------------------------------

const ring = keyframes`
  0%, 100% { transform: rotate(0); }
  10% { transform: rotate(14deg); } 20% { transform: rotate(-12deg); }
  30% { transform: rotate(8deg); } 40% { transform: rotate(-6deg); } 50% { transform: rotate(0); }
`;

const Wrap = styled.div` position: relative; `;

const BellButton = styled.button`
  background: transparent;
  border: 1px solid #333;
  border-radius: 999px;
  width: 42px; height: 42px;
  font-size: 1.15rem;
  cursor: pointer;
  position: relative;
  ${(p) => p.$alert && css`animation: ${ring} 2.4s ease-in-out infinite; border-color: #ffd700;`}
  &:hover { border-color: #ffd700; }
`;

const Badge = styled.span`
  position: absolute; top: -6px; right: -6px;
  background: #ffd700; color: #000;
  border-radius: 999px; min-width: 18px; height: 18px;
  font-size: 0.7rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center; padding: 0 4px;
`;

const Panel = styled.div`
  position: absolute; right: 0; top: 52px; z-index: 50;
  width: min(420px, 92vw);
  background: #161616; border: 1px solid #333; border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.6);
  padding: 0.5rem;
`;

const Item = styled.div`
  padding: 0.85rem 0.9rem;
  border-bottom: 1px solid #242424;
  &:last-child { border-bottom: none; }
  .t { color: #ffd700; font-weight: 600; font-size: 0.92rem; margin-bottom: 0.25rem; }
  .b { color: #bbb; font-size: 0.82rem; line-height: 1.5; margin-bottom: 0.55rem; }
  button {
    background: linear-gradient(45deg, #ffd700, #f5c400); color: #000; border: none;
    border-radius: 8px; padding: 0.4rem 0.9rem; font-weight: 700; font-size: 0.8rem; cursor: pointer;
    &:disabled { opacity: 0.5; cursor: default; }
  }
`;

const Empty = styled.div`
  padding: 1.2rem; text-align: center; color: #777; font-size: 0.85rem;
`;

const fmt18 = (x) => formatNumber(Number(ethers.formatEther(x)), 0);

const NoticeBell = ({ signer, account }) => {
  const { finds, refresh } = useNotices(account);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!account) return null;

  const act = async (label, fn) => {
    setBusy(true);
    toast.loading(label, { id: 'notice' });
    try {
      await fn((m) => toast.loading(m, { id: 'notice' }));
      toast.success('Done — it’s in your wallet.', { id: 'notice' });
      refresh();
    } catch (e) {
      toast.error(`${label} failed: ${e?.reason || e?.shortMessage || e?.message || 'unknown'}`, { id: 'notice', duration: 8000 });
    } finally { setBusy(false); }
  };
  const c = (addr, abi) => new ethers.Contract(addr, abi, signer);
  const ov = () => txOverrides(signer.provider);

  const notices = [];
  if (finds) {
    if (finds.daiRad > 0n) {
      const amt = formatNumber(Number(ethers.formatUnits(finds.daiRad, 45)), 4);
      notices.push({
        id: 'dai',
        title: `${amt} JDAI is sitting inside the vault engine`,
        body: 'JDAI you minted or deposited that was never withdrawn to your wallet as tokens. It never expires and no one can take it.',
        sigs: finds.daiSigs,
        action: 'Withdraw to wallet',
        run: () => act('Withdrawing JDAI', async (step) => {
          const vat = c(CONTRACTS.VAT, ABIS.VAT);
          const needHope = (await vat.can(account, CONTRACTS.DAIJOIN)) === 0n;
          if (needHope) { step('Sign 1 of 2 — one-time authorization…'); await (await vat.hope(CONTRACTS.DAIJOIN, await ov())).wait(); }
          step(needHope ? 'Sign 2 of 2 — withdrawing…' : 'Sign 1 of 1 — withdrawing…');
          const wad = finds.daiRad / 10n ** 27n; // rad -> wad, rounded down
          await (await c(CONTRACTS.DAIJOIN, ABIS.DAIJOIN).exit(account, wad, await ov())).wait();
        })
      });
    }
    if (finds.gemPls > 0n) {
      notices.push({
        id: 'gemPls',
        title: `${fmt18(finds.gemPls)} PLS is waiting inside the vault engine`,
        body: 'Collateral you deposited or unlocked (Classic vault) that was never withdrawn. It never expires and no one can take it.',
        action: 'Withdraw as PLS',
        run: () => act('Withdrawing PLS', async (step) => {
          step('Sign 1 of 1 — withdrawing to your wallet…');
          await (await c(CONTRACTS.ETHJOIN, ABIS.ETHJOIN).exit(account, finds.gemPls, await ov())).wait();
        })
      });
    }
    if (finds.gemWpls > 0n) {
      notices.push({
        id: 'gemWpls',
        title: `${fmt18(finds.gemWpls)} PLS (wrapped) is waiting inside the vault engine`,
        body: 'Collateral credited to you — for example from an auction purchase. Withdrawing unwraps it back to native PLS.',
        sigs: 2,
        action: 'Withdraw as PLS',
        run: () => act('Withdrawing PLS', async (step) => {
          step('Sign 1 of 2 — withdrawing from the vault engine…');
          await (await c(CONTRACTS.GEMJOIN_WPLS_A, ABIS.GEMJOIN).exit(account, finds.gemWpls, await ov())).wait();
          step('Sign 2 of 2 — unwrapping to native PLS…');
          const bal = await c(CONTRACTS.PLS, ABIS.PLS).balanceOf(account);
          await (await c(CONTRACTS.PLS, ABIS.PLS).withdraw(bal, await ov())).wait();
        })
      });
    }
    for (const l of finds.cdpLeftovers) {
      notices.push({
        id: `cdp${l.cdp}`,
        title: l.liquidated
          ? `Your vault #${l.cdp} was liquidated — ${fmt18(l.gem)} PLS of collateral was left over`
          : `Vault #${l.cdp} is holding ${fmt18(l.gem)} PLS of unlocked collateral`,
        body: l.liquidated
          ? 'The auction didn’t need all of your collateral. What remains is still yours — it never expires and no one can take it. One signature brings it back as native PLS.'
          : 'Collateral sitting in your vault wrapper, not locked and not in your wallet. One signature brings it back as native PLS.',
        action: 'Reclaim to wallet',
        run: () => act('Reclaiming collateral', async (step) => {
          step('Sign 1 of 1 — reclaiming as native PLS…');
          const pa = new ethers.Interface(ABIS.PROXY_ACTIONS);
          const data = pa.encodeFunctionData('exitETH',
            [CONTRACTS.CDP_MANAGER, CONTRACTS.GEMJOIN_WPLS_A, l.cdp, l.gem]);
          await (await c(finds.proxy, ABIS.DS_PROXY).execute(CONTRACTS.PROXY_ACTIONS, data, await ov())).wait();
        })
      });
    }
    if (finds.walletWpls > 0n) {
      notices.push({
        id: 'wpls',
        title: `${fmt18(finds.walletWpls)} wrapped PLS in your wallet`,
        body: 'WPLS is PLS in a wrapper (used by vaults and auctions). Unwrap any time — 1:1, no fee beyond gas.',
        action: 'Unwrap to PLS',
        run: () => act('Unwrapping WPLS', async (step) => {
          step('Sign 1 of 1 — unwrapping…');
          await (await c(CONTRACTS.PLS, ABIS.PLS).withdraw(finds.walletWpls, await ov())).wait();
        })
      });
    }
  }

  return (
    <Wrap>
      <BellButton $alert={notices.length > 0} onClick={() => setOpen(!open)} title="Notices">
        🔔
        {notices.length > 0 && <Badge>{notices.length}</Badge>}
      </BellButton>
      {open && (
        <Panel>
          {notices.length === 0 ? (
            <Empty>No notices — nothing of yours is waiting anywhere.</Empty>
          ) : notices.map((n) => (
            <Item key={n.id}>
              <div className="t">{n.title}</div>
              <div className="b">{n.body}</div>
              {/* say up front when more than one wallet prompt is coming;
                  stay silent for a single signature */}
              <button onClick={n.run} disabled={busy || !signer}>
                {n.action}{n.sigs > 1 ? ` · ${n.sigs} signatures` : ''}
              </button>
            </Item>
          ))}
        </Panel>
      )}
    </Wrap>
  );
};

export default NoticeBell;
